import type { PanInfo } from 'motion/react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import type { ReactNode } from 'react';
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PixelIcon from './PixelIcon';
import { useSound } from '@/contexts/SoundContext';
import {
  FONT, FONT_SIZE, SPACING, RADIUS, COLOR, SHADOW, BLUR, Z_INDEX, TOUCH, MODAL_SIZE,
  getVariantColors,
} from '@/config/designTokens';

// Hook to detect mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth <= 767);
    checkIsMobile();

    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

export interface ModalTab {
  id: string;
  label: string;
  icon: string;
  count?: number;
  badge?: string;
}

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info';
  size?: 'small' | 'medium' | 'large' | 'fullscreen';
  showCloseButton?: boolean;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  children: ReactNode;
  className?: string;
  headerContent?: ReactNode;
  footerContent?: ReactNode;
  tabs?: ModalTab[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  showMobileBar?: boolean;
  mobileBarActions?: ReactNode;
  layoutId?: string; // For shared layout animations from mobile nav
  sourceButtonId?: string; // ID of the button that opened this modal
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  variant = 'default',
  size = 'medium',
  showCloseButton = true,
  closeOnBackdrop = true,
  closeOnEscape = true,
  children,
  className = '',
  headerContent,
  footerContent,
  tabs,
  activeTab,
  onTabChange,
  showMobileBar = false,
  mobileBarActions,
  layoutId,
  sourceButtonId: _sourceButtonId,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const isMobile = useIsMobile();
  const prefersReducedMotion = useReducedMotion();
  const shouldAnimate = !prefersReducedMotion;
  const { playModalOpen, playModalClose } = useSound();
  const shellClassName = [
    'modal-shell',
    `modal-shell--${variant}`,
    `modal-shell--${size}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Store previous focus element and play open/close sounds
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      previousFocusRef.current = document.activeElement as HTMLElement;
      playModalOpen();
    } else if (!isOpen && wasOpenRef.current) {
      playModalClose();
      if (previousFocusRef.current) {
        previousFocusRef.current.focus();
        previousFocusRef.current = null;
      }
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, playModalOpen, playModalClose]);

  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Handle body scroll lock
  useEffect(() => {
    if (isOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      return () => {
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      };
    }
  }, [isOpen]);

  // Handle backdrop click
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (closeOnBackdrop && event.target === event.currentTarget) {
      onClose();
    }
  };

  // Focus management with tab trapping
  useEffect(() => {
    if (isOpen && contentRef.current) {
      const focusableElements = contentRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      // Focus first element after animation
      const focusTimer = setTimeout(() => {
        if (firstElement) firstElement.focus();
      }, 100);

      // Tab trap handler
      const handleTabKey = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement?.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement?.focus();
            e.preventDefault();
          }
        }
      };

      document.addEventListener('keydown', handleTabKey);
      return () => {
        clearTimeout(focusTimer);
        document.removeEventListener('keydown', handleTabKey);
      };
    }
  }, [isOpen]);

  // Size configurations from design tokens
  const sizeConfig = MODAL_SIZE;

  // Variant colors from design tokens
  const colors = getVariantColors(variant, isMobile);

  // Handle drag to close on mobile
  const handleDragEnd = (_: any, info: PanInfo) => {
    if (info.offset.y > 100 && info.velocity.y > 0) {
      onClose();
    }
  };

  const modalContent = (
    <AnimatePresence mode="wait">
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="modal-backdrop-layer"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: Z_INDEX.modalBackdrop,
              backgroundColor: COLOR.backdropOverlay,
              backdropFilter: BLUR.backdrop,
            }}
            onClick={handleBackdropClick}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: shouldAnimate ? 0.22 : 0.01 }}
          />

          {/* Modal Content Container */}
          <motion.div
            className="modal-shell-frame"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: Z_INDEX.modal,
              display: 'flex',
              alignItems: isMobile ? 'stretch' : 'center',
              justifyContent: isMobile ? 'stretch' : 'center',
              padding: isMobile ? 0 : SPACING.lg,
              pointerEvents: 'none',
            }}
          >
            <motion.div
              ref={contentRef}
              className={shellClassName}
              data-modal-variant={variant}
              data-modal-size={size}
              layoutId={layoutId}
              style={{
                position: 'relative',
                width: isMobile ? '100vw' : '100%',
                height: isMobile ? '100vh' : 'auto',
                maxWidth: isMobile ? 'none' : sizeConfig[size].maxWidth,
                maxHeight: isMobile ? 'none' : size === 'fullscreen' ? '100%' : '90vh',
                display: 'flex',
                flexDirection: 'column',
                background: colors.bgGradient,
                borderRadius: isMobile ? 0 : size === 'fullscreen' ? 0 : RADIUS.modal,
                border: isMobile ? 'none' : `4px solid ${colors.border}`,
                borderTop: isMobile ? `4px solid ${colors.border}` : undefined,
                boxShadow: SHADOW.modal,
                fontFamily: FONT.heading,
                color: COLOR.textPrimary,
                overflow: 'hidden',
                pointerEvents: 'auto',
              }}
              onClick={e => e.stopPropagation()}
              initial={shouldAnimate ? { opacity: 0, scale: 0.92, y: 36 } : { opacity: 1, scale: 1, y: 0 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={shouldAnimate ? { opacity: 0, scale: 0.98, y: 18 } : { opacity: 0 }}
              transition={
                shouldAnimate
                  ? {
                      type: 'spring',
                      stiffness: 300,
                      damping: 30,
                      mass: 0.8,
                    }
                  : { duration: 0.01 }
              }
              drag={isMobile ? 'y' : false}
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={0.2}
              onDragEnd={handleDragEnd}
            >
              {shouldAnimate && (
                <div className={`modal-delight modal-delight--${variant}`} aria-hidden="true">
                  <span className="modal-delight__wipe modal-delight__wipe--1" />
                  <span className="modal-delight__wipe modal-delight__wipe--2" />
                  <span className="modal-delight__wipe modal-delight__wipe--3" />
                  <span className="modal-delight__splash modal-delight__splash--1" />
                  <span className="modal-delight__splash modal-delight__splash--2" />
                  <span className="modal-delight__splash modal-delight__splash--3" />
                  <span className="modal-delight__splash modal-delight__splash--4" />
                </div>
              )}

              {/* Header */}
              {(title || subtitle || showCloseButton || headerContent || tabs) && (
                <motion.div
                  style={{
                    padding: isMobile
                      ? `max(env(safe-area-inset-top), ${SPACING.lg}) ${SPACING.xl} 0`
                      : `${SPACING.xxl} ${SPACING.xxxl} 0`,
                    borderBottom: tabs ? 'none' : `2px solid ${colors.border}`,
                  }}
                  initial={shouldAnimate ? { opacity: 0, y: -20 } : { opacity: 1, y: 0 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: shouldAnimate ? 0.08 : 0, duration: shouldAnimate ? 0.25 : 0.01 }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      marginBottom: tabs ? SPACING.lg : SPACING.xl,
                    }}
                  >
                    {/* Mobile back button */}
                    {isMobile && (
                      <motion.button
                        onClick={onClose}
                        aria-label="Go back"
                        type="button"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: TOUCH.backButton,
                          height: TOUCH.backButton,
                          padding: SPACING.sm,
                          background: `linear-gradient(135deg, ${COLOR.secondary} 0%, #3498db 50%, ${COLOR.secondary} 100%)`,
                          border: `3px solid ${COLOR.secondaryBorder}`,
                          borderRadius: RADIUS.sm,
                          color: COLOR.textWhite,
                          marginRight: SPACING.lg,
                          flexShrink: 0,
                          cursor: 'pointer',
                          boxShadow: SHADOW.mobileBack,
                        }}
                        whileHover={{
                          scale: 1.05,
                          boxShadow: `0 6px 20px rgba(69, 183, 209, 0.3)`,
                        }}
                        whileTap={{
                          scale: 0.95,
                        }}
                      >
                        <PixelIcon name="arrow-left" size={24} />
                      </motion.button>
                    )}

                    <div style={{ flex: 1 }}>
                      {title && (
                        <motion.h2
                          id="modal-title"
                          style={{
                            fontSize: isMobile ? FONT_SIZE.modalTitleMobile : FONT_SIZE.modalTitle,
                            fontWeight: 'normal',
                            color: COLOR.textPrimary,
                            margin: 0,
                            textShadow: SHADOW.text,
                            letterSpacing: '1px',
                            fontFamily: FONT.heading,
                            textTransform: 'uppercase',
                          }}
                        >
                          {title}
                        </motion.h2>
                      )}
                      {subtitle && (
                        <motion.p
                          id="modal-subtitle"
                          style={{
                            fontSize: FONT_SIZE.modalSubtitle,
                            color: COLOR.textSecondary,
                            margin: `${SPACING.sm} 0 0 0`,
                            fontFamily: FONT.heading,
                            letterSpacing: '0.5px',
                          }}
                        >
                          {subtitle}
                        </motion.p>
                      )}
                      {headerContent && (
                        <motion.div style={{ marginTop: SPACING.md }}>
                          {headerContent}
                        </motion.div>
                      )}
                    </div>

                    {showCloseButton && !isMobile && (
                      <motion.button
                        onClick={onClose}
                        aria-label="Close modal"
                        type="button"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: SPACING.sm,
                          padding: `${SPACING.md} 15px`,
                          background: `linear-gradient(135deg, ${COLOR.closeButton} 0%, ${COLOR.closeButtonHover} 50%, ${COLOR.closeButton} 100%)`,
                          border: `3px solid ${COLOR.closeButtonBorder}`,
                          borderRadius: RADIUS.sm,
                          color: COLOR.textWhite,
                          fontSize: FONT_SIZE.caption,
                          fontWeight: 'normal',
                          cursor: 'pointer',
                          fontFamily: FONT.heading,
                          letterSpacing: '1px',
                          textTransform: 'uppercase',
                          boxShadow: SHADOW.closeButton,
                        }}
                        whileHover={{
                          backgroundColor: COLOR.closeButtonHover,
                          scale: 1.01,
                          y: -2,
                          boxShadow: SHADOW.closeButtonHover,
                        }}
                        whileTap={{
                          scale: 0.98,
                          y: -1,
                        }}
                      >
                        <PixelIcon name="close" size={16} />
                        <span>Close</span>
                        <kbd
                          style={{
                            padding: `${SPACING.xs} ${SPACING.sm}`,
                            background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.5) 100%)',
                            color: COLOR.textWhite,
                            borderRadius: RADIUS.sm,
                            fontSize: FONT_SIZE.micro,
                            fontWeight: 'bold',
                            border: '2px solid rgba(255, 255, 255, 0.3)',
                            fontFamily: FONT.heading,
                            minWidth: '35px',
                            textAlign: 'center',
                            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.4), inset 0 1px 2px rgba(255, 255, 255, 0.2)',
                          }}
                        >
                          ESC
                        </kbd>
                      </motion.button>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Tabs */}
              {tabs && tabs.length > 1 && onTabChange && (
                <motion.div
                  className={`modal-tabs-row${isMobile ? ' modal-tabs-row--mobile' : ''}`}
                  style={{
                    borderBottom: `2px solid ${colors.border}`,
                  }}
                  initial={shouldAnimate ? { opacity: 0, y: -10 } : { opacity: 1, y: 0 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: shouldAnimate ? 0.12 : 0, duration: shouldAnimate ? 0.3 : 0.01 }}
                >
                  {tabs.map((tab, index) => (
                    <motion.button
                      key={tab.id}
                      onClick={() => onTabChange(tab.id)}
                      type="button"
                      className={`modal-tab-button${activeTab === tab.id ? ' modal-tab-button--active' : ''}`}
                      style={
                        {
                          ['--tab-active-color' as string]: colors.primary,
                        } as React.CSSProperties
                      }
                      whileHover={
                        activeTab !== tab.id
                          ? {
                              y: -1,
                            }
                          : {}
                      }
                      whileTap={{ scale: 0.98 }}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.05 * index }}
                      aria-label={tab.label}
                      aria-current={activeTab === tab.id ? 'page' : undefined}
                    >
                      <PixelIcon name={tab.icon} size={18} className="modal-tab-icon" />
                      <span className={`modal-tab-label${isMobile ? ' sr-only' : ''}`}>{tab.label}</span>
                      {tab.count !== undefined && (
                        <span className="modal-tab-count" aria-label={`${tab.count} items`}>
                          {tab.count}
                        </span>
                      )}
                      {tab.badge && <span className="modal-tab-badge" aria-hidden="true" />}
                    </motion.button>
                  ))}
                </motion.div>
              )}

              {/* Body */}
              <motion.div
                className="modal-scroll-body"
                style={{
                  flex: 1,
                  padding: isMobile ? SPACING.lg : sizeConfig[size].padding,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  boxSizing: 'border-box',
                  maxWidth: '100%',
                  scrollbarWidth: 'thin',
                  scrollbarColor: `${colors.border} transparent`,
                }}
                initial={shouldAnimate ? { opacity: 0, y: 20 } : { opacity: 1, y: 0 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: shouldAnimate ? 0.16 : 0, duration: shouldAnimate ? 0.35 : 0.01 }}
              >
                {children}
              </motion.div>

              {/* Footer */}
              {footerContent && (
                <motion.div
                  className="modal-footer-row"
                  style={{
                    padding: isMobile ? `${SPACING.lg} ${SPACING.xl}` : `${SPACING.xl} ${SPACING.xxxl}`,
                    borderTop: `2px solid ${colors.border}`,
                    backgroundColor: COLOR.surfaceDark,
                  }}
                  initial={shouldAnimate ? { opacity: 0, y: 20 } : { opacity: 1, y: 0 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: shouldAnimate ? 0.2 : 0, duration: shouldAnimate ? 0.3 : 0.01 }}
                >
                  {footerContent}
                </motion.div>
              )}

              {/* Mobile Bottom Bar */}
              {showMobileBar && isMobile && (
                <motion.div
                  className="modal-mobile-actions-row"
                  style={{
                    display: 'flex',
                    position: 'sticky',
                    bottom: 0,
                    padding: SPACING.lg,
                    backgroundColor: COLOR.surfaceDark,
                    borderTop: `2px solid ${colors.border}`,
                    gap: SPACING.md,
                    justifyContent: 'space-around',
                    paddingBottom: `max(env(safe-area-inset-bottom), ${SPACING.lg})`,
                  }}
                  initial={{ y: 100 }}
                  animate={{ y: 0 }}
                  transition={shouldAnimate ? { type: 'spring', stiffness: 300, damping: 30 } : { duration: 0.01 }}
                >
                  {mobileBarActions}
                </motion.div>
              )}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default Modal;
